import { ArticleType } from "@coworker/types/lib/tasks/constants";
import { Article, ArticleIdNbrArticles, Articles } from "../types/article";
import { ItemType } from "@coworker/apprestructured/src/shared/constants/itemType";

/**
 * Checks if the given article type is a local combination article.
 * @param articleType - The type of the article.
 * @returns True if the article type is "SCI", otherwise false.
 */
export const isLocalCombinationArticle = (articleType: ArticleType) =>
  articleType === "SCI";

export const isCombinationArticleValid = (
  combinationArticle: any
): combinationArticle is Article => {
  return !!(combinationArticle && Object.keys(combinationArticle).length > 0);
};

export const isArticleInCombination = (article: Article): boolean => {
  return !!article.parentArticleCombinationNumber;
};

export function isEmptyCombinationArticle(article: Article) {
  return (
    article?.articlesCountInCombination === 0 &&
    article.productArticleType === ItemType.LOCAL_COMBINATION
  );
}

export function processArticles(articles: Articles) {
  const activeArticles = articles.filter(
    (article) => !article.parentArticleCombinationNumber
  );

  const articlesInCombination = articles.filter(
    (article) => article.parentArticleCombinationNumber
  );

  const combinationArticlesCollection = articles.filter(
    (article) => article.productArticleType === ItemType.LOCAL_COMBINATION
  );

  const groupedCombinationArticles = articlesInCombination.reduce<
    Record<string, Articles>
  >((acc, article) => {
    const parent = article.parentArticleCombinationNumber!;
    if (!acc[parent]) {
      acc[parent] = [];
    }
    acc[parent].push(article);
    return acc;
  }, {});

  return {
    activeArticles,
    articlesInCombination,
    groupedCombinationArticles,
    combinationArticlesCollection,
  };
}

export const extractCombinationArticles = (articles: Articles) => {
  // Filter out combination articles to avoid calls to backend with all article IDs in media
  const { combinationArticlesCollection } = processArticles(articles);
  return (
    combinationArticlesCollection
      .map((article: Article) => article?.productArticleId)
      .join(",") || ""
  );
};

/**
 * Returns articles that are not part of a combination (no parentArticleCombinationNumber)
 */
export function filterMainArticles(articles: Article[]) {
  return articles.filter(
    (article) =>
      article.parentArticleCombinationNumber === "" ||
      article.parentArticleCombinationNumber === null
  );
}

export const selectedArticleIdsToArticles = (
  articles: Articles,
  selectedArticleIdsNbArticles: ArticleIdNbrArticles[],
  articlesInCombination: Articles
) => {
  return !articles || !selectedArticleIdsNbArticles
    ? []
    : (selectedArticleIdsNbArticles
        .flatMap((item) => {
          const foundArticle = articles.find(
            (article) => article.id === item.articleId
          );
          if (foundArticle) {
            // Check if selected article is part of a local combination then add all sub-articles to the selection
            if (
              isLocalCombinationArticle(
                foundArticle.productArticleType as ArticleType
              )
            ) {
              const articleInCombination = articlesInCombination.filter(
                (article) =>
                  article.parentArticleCombinationNumber ===
                  foundArticle.productArticleId
              );

              return [
                foundArticle,
                ...articleInCombination.map((article) => {
                  article.newNbrArticles = item.nbrArticles;
                  return article;
                }),
              ];
            }
            foundArticle.newNbrArticles = item.nbrArticles;
          }
          return foundArticle;
        })
        .filter((article) => !!article) as Articles);
};
